import { AfterViewInit, Component, ElementRef, OnInit, inject, viewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';

import autocomplete from 'autocompleter';
import { loadStripe } from '@stripe/stripe-js/pure';
import { Stripe, StripeCardElement } from '@stripe/stripe-js';

import { environment } from '../../environments/environment';
import { AccountService, AddAccount, Subscription } from '../shared/services/account.service';
import { Institution, InstitutionService } from '../products/services/institution.service';
import { EmailValidator } from '../shared/validators/email.validator';
import { InstitutionValidator } from '../shared/validators/institution.validator';
import { PromoCodeValidator } from '../shared/validators/promo-code.validator';
import { PartnerService } from '../shared/services/partner.service';
import { PlanComponent } from '../pricing/plan/plan.component';

@Component({
  selector: 'app-signup',
  standalone: true,
  imports: [ReactiveFormsModule, PlanComponent],
  templateUrl: './signup.component.html',
  styleUrl: './signup.component.scss'
})
export class SignupComponent implements OnInit, AfterViewInit {
  firstNameInput = viewChild.required('firstNameInput', { read: ElementRef });
  institutionInput = viewChild.required('institutionInput', { read: ElementRef });

  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private partnerService = inject(PartnerService);
  private accountService = inject(AccountService);
  private institutionService = inject(InstitutionService);

  private formBuilder = inject(FormBuilder);
  form = this.formBuilder.group({
    firstName: ['', Validators.required],
    lastName: ['', Validators.required],
    email: ['', [Validators.required, Validators.pattern("^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,4}$")], EmailValidator.createValidator(this.accountService)],
    phone: ['', Validators.required],
    title: ['', Validators.required],
    promoCode: ['', [], PromoCodeValidator.createValidator(this.accountService)],
    institution: ['', Validators.required],
    institutionIid: this.formBuilder.control(
      '',
      {
        validators: [Validators.required],
        asyncValidators: [
          InstitutionValidator.createInfoChecker(this.accountService, (info) => {
            this.institutionExistsInfo = info.institutionExists;
            this.institutionHasNonMaturity = info.hasNonMaturity;
            this.institutionHasPeerComparison = info.hasPeerComparison;

            this.institutionHasSignUpProduct = this.institutionExistsInfo && (
              (this.institutionHasNonMaturity && this.planType == 'NonMaturityDepositsStandard') ||
              (this.institutionHasPeerComparison && this.planType == 'PeerComparisonStandard') ||
              (this.institutionHasPeerComparison && this.institutionHasNonMaturity && this.planType == 'BundleYearly')
            )
          })
        ],
        updateOn: 'change'
      }
    ),
    subscription: ['', Validators.required]
  },
    { updateOn: 'blur' }
  );

  private stripe: Stripe | null = null;
  private card?: StripeCardElement;

  planType = '';
  subscriptions: Subscription[] = [];
  lockInstitution = false;
  cardError?: string;
  submitDisabled = false;
  action: string = 'signUp';
  institutionExistsInfo = false;
  institutionHasNonMaturity = false;
  institutionHasPeerComparison = false;
  institutionHasSignUpProduct = false;

  ngOnInit(): void {
    this.route.url.subscribe(params => {
      const partnerCode = params[1]?.path;
      if (partnerCode != undefined) {
        this.partnerService.setPartner(partnerCode);
      }
    });

    this.route.queryParams
      .subscribe(params => {
        this.planType = params['plan'] ?? 'PeerComparisonBasic';
      });

    this.accountService.getSubscriptions(this.planType)
      .subscribe(subscriptions => {
        this.subscriptions = subscriptions;
        if (this.planType == 'PeerComparisonBasic') {
          this.form.patchValue({ subscription: this.subscriptions[0].id });
        }
      });
  }

  ngAfterViewInit(): void {
    autocomplete<Institution>({
      input: this.institutionInput().nativeElement,
      emptyMsg: 'No institutions found',
      minLength: 3,
      fetch: (text: string, update: (items: Institution[]) => void) => {
        const list = this.institutionService.searchInstitutions(text).filter(i => i.active);
        update(list);
      },
      onSelect: (item: Institution) => {
        this.institutionInput().nativeElement.value = item.label;
        this.form.patchValue({ institution: item.label, institutionIid: item.iid.toString() });
        this.lockInstitution = true;
        this.institutionInput().nativeElement.blur();
      }
    });

    if (this.planType != 'PeerComparisonBasic') {
      this.initStripe();
    }

    setTimeout(() => {
      this.firstNameInput().nativeElement.focus();
    });
  }

  clearInstitution() {
    this.institutionInput().nativeElement.value = '';
    this.institutionInput().nativeElement.focus();
    this.form.patchValue({ institutionIid: '' });
    this.lockInstitution = false;
  }

  async initStripe() {
    const style = {
      base: {
        iconColor: '#FFFFFF',
        color: '#FFFFFF',
        fontWeight: '400',
        fontFamily: 'Open Sans, sans-serif',
        fontSize: '16px',
        fontSmoothing: 'antialiased',
        ':-webkit-autofill': {
          color: '#111111',
        },
        '::placeholder': {
          color: '#FFFFFF',
        },
      },
      invalid: {
        iconColor: '#FF99AA',
        color: '#FF99AA',
      },
    }

    this.stripe = await loadStripe(environment.stripePublicKey);
    const elements = this.stripe?.elements();
    this.card = elements?.create('card', { style: style });
    this.card?.mount('#card-element');
    this.card?.on('change', (event: any) => {
      this.cardError = event.error?.message;
    });
  }

  planTypeLabels: { [key: string]: string } = {
    NonMaturityDepositsStandard: 'Non Maturity Deposits',
    PeerComparisonStandard: 'Peer Comparison',
    BundleYearly: 'Bundle'
  };

  get planLabel(): string {
    return this.planTypeLabels[this.planType] || this.planType;
  }

  async onSubmit(action: string) {
    this.submitDisabled = true;

    const institutionIid = this.form.value.institutionIid;
    const firstName = this.form.value.firstName;
    const lastName = this.form.value.lastName;
    const email = this.form.value.email;
    const phone = this.form.value.phone;
    const title = this.form.value.title;
    const name = firstName + ' ' + lastName;
    const promotCode = this.form.value.promoCode;
    const subscription = this.form.value.subscription;
    const stripeId = this.subscriptions.find(s => s.id == subscription)?.stripeId ?? '';
    const planTypeClaim = this.planType;


    const model: AddAccount = {
      organizationIid: institutionIid!,
      email: email!,
      firstName: firstName!,
      lastName: lastName!,
      phone: phone!,
      title: title!,
      promoCode: promotCode!,
      subscriptionId: subscription!,
      planType: planTypeClaim!,
    };

    if (action == 'promo') {
      this.accountService.addAccount(model)
        .subscribe(() => {
          this.router.navigate(['/signup/thankyou']);
        });
    }

    if (email != null) {
      const { error: paymentMethodError, paymentMethod } = await this.stripe!.createPaymentMethod({
        type: 'card',
        card: this.card!,
        billing_details: {
          name: name,
          email: email
        }
      });

      if (paymentMethodError) {
        this.cardError = paymentMethodError.message;
        this.submitDisabled = false;
        return;
      }

      if (action == 'signUp') {
        this.accountService.addBillingAccount(email!, name, stripeId, paymentMethod.id)
          .subscribe(billingAccount => {
            this.stripe?.confirmCardPayment(billingAccount.clientSecret, {
              payment_method: paymentMethod.id
            }).then((result) => {
              if (result.error) {
                this.cardError = result.error.message;
                this.submitDisabled = false;
              }
              else {
                model.billingCustomerId = billingAccount.customerId,
                  model.billingSubscriptionId = billingAccount.subscriptionId,

                  this.accountService.addAccount(model)
                    .subscribe(() => {
                      this.router.navigate(['/signup/thankyou']);
                    });
              }
            });
          });

      }
      else if (action == 'register') {
        this.accountService.addUserAccountToExitingOrganization(model)
          .subscribe(() => {
            this.router.navigate(['/signup/thankyou']);
          });
      }
    }
  }
}
